import {
  DoubleSide,
  NoBlending,
  NormalBlending,
  type BufferAttribute,
  type BufferGeometry,
  type InterleavedBufferAttribute,
  type Material,
  type Mesh,
  type Object3D,
} from "three";
import { MeshBVH } from "three-mesh-bvh";
import type {
  BoundsTreeJobData,
  BoundsTreeRequest,
  BoundsTreeResponse,
  BoundsTreeResultData,
} from "./boundsTree.worker";

/**
 * Builds a spatial index for every eligible mesh geometry it is handed, in a
 * Web Worker, and attaches it as `geometry.boundsTree` — which
 * `boundsTreeRaycast.ts` then raycasts through (2026-10-07).
 *
 * Off the main thread because the build is not small: Apollo 11 Hatch Door's
 * 850 geometries (3.57 M triangles) took 789 ms on the main thread, its largest
 * single geometry 21 ms, and one scanned mesh of a few million triangles would
 * be a visible freeze on its own. Here the main thread only copies each
 * geometry's positions and index out and takes back what the worker built.
 *
 * Until a geometry's tree arrives, its picks take three's own raycast, which is
 * what every pick did before; so there is nothing to wait for and nothing to
 * get wrong while it builds.
 *
 * **Where a tree keeps its triangle order.** A tree's leaves are runs of
 * triangles in ITS order. By default the geometry's own index buffer is sorted
 * into that order (in place, so the order costs no memory at all); the
 * alternative is a separate list of triangle numbers, 2 or 4 bytes a triangle,
 * which on Apollo was most of the index's memory. Sorting changes only the
 * sequence the GPU draws a mesh's triangles in — each keeps its corners and
 * winding — which is invisible for a single-sided solid surface and can show
 * on a see-through or double-sided one. So the list is kept for:
 *  - a geometry drawn with a material that can show triangle order
 *    (`isOrderSensitive`);
 *  - a geometry with no index to sort;
 *  - an index whose memory overlaps a DIFFERENT index's (`analyzeIndexSharing`);
 *  - a geometry with coincident triangles, which only the worker checks.
 * An index shared outright — one glTF accessor reused by several parts that
 * differ only in their vertices, 568 of Apollo's 845 indexed geometries — gets
 * ONE job: built once for the first of them, the others reusing its leaves with
 * bounds refitted to their own vertices (`boundsTree.worker.ts`). So the group
 * shares one sorted index or one list. It is sorted only when every geometry
 * in it is safe to sort, all or nothing, because they all draw from that index.
 *
 * The copy matters: `GLTFLoader` hands out positions and indices as VIEWS into
 * a bufferView that many meshes share, and posting a view to a worker clones
 * the whole buffer behind it. Each job is sliced out first, and the slices are
 * transferred, not copied again.
 */

/**
 * Below this, three's own triangle loop costs about what a walk of the tree
 * does, and a tree would only add a message and memory.
 */
const K_MIN_TRIANGLES = 256;
/**
 * Per message: about 20 ms of worker time, and a copy short enough to sit
 * inside a frame.
 */
const K_MAX_TRIANGLES_PER_REQUEST = 100_000;
const K_MAX_GEOMETRIES_PER_REQUEST = 512;
/** Bounds how much copied geometry is waiting in the worker at once. */
const K_MAX_REQUESTS_IN_FLIGHT = 2;

type PositionArray = BoundsTreeJobData["position"];
type IndexArray = NonNullable<BoundsTreeJobData["index"]>;

interface QueuedJob {
  /**
   * Weak, so a project unloaded mid-build leaves nothing pinned here. More
   * than one only for an in-place group sharing one index: the first is
   * sorted, the rest are refitted to it.
   */
  readonly geometries: ReadonlyArray<WeakRef<BufferGeometry>>;
  /** Sort the shared index into the tree's order (no triangle list). */
  readonly inPlace: boolean;
}

interface InFlightMember {
  readonly geometry: WeakRef<BufferGeometry>;
  /** What the tree was built from: a tree only fits the geometry it came from. */
  readonly position: BufferAttribute | InterleavedBufferAttribute;
  readonly positionVersion: number;
}

interface InFlightJob {
  readonly members: ReadonlyArray<InFlightMember>;
  readonly index: BufferAttribute | null;
  readonly indexVersion: number;
}

interface GeometryUse {
  /** Some mesh draws it with a material whose result depends on triangle order. */
  orderSensitive: boolean;
  /** Some mesh raycasts it through `Mesh.raycast`, so a tree would be read. */
  raycast: boolean;
}

export class MeshBoundsTreeBuilder {
  private m_worker: Worker | null = null;
  private m_unavailable = false;
  private m_queue: QueuedJob[] = [];
  private m_queueHead = 0;
  private m_queued = new WeakSet<BufferGeometry>();
  private readonly m_inFlight = new Map<number, ReadonlyArray<InFlightJob>>();
  private m_nextRequestId = 1;
  private m_pumpScheduled = false;

  /**
   * Queues every eligible geometry under `root` that has no tree yet, and
   * decides for each whether its index may be sorted in place. Cheap: one walk
   * of the subtree; the copying happens later, a request at a time.
   */
  enqueue(root: Object3D): void {
    if (this.m_unavailable) return;
    const uses = new Map<BufferGeometry, GeometryUse>();
    root.traverse((node) => {
      const mesh = node as Mesh;
      if (mesh.isMesh !== true) return;
      let use = uses.get(mesh.geometry);
      if (!use) {
        use = { orderSensitive: false, raycast: false };
        uses.set(mesh.geometry, use);
      }
      if (!hasOwnRaycast(mesh)) use.raycast = true;
      // A batch's outline proxy draws only into an outline mask; its order is
      // never seen, and its geometry is the source mesh's.
      if (mesh.userData.campfireBatchOwned !== true && isOrderSensitive(mesh.material)) {
        use.orderSensitive = true;
      }
    });

    const isCandidate = (geometry: BufferGeometry): boolean =>
      uses.get(geometry)!.raycast
      && !geometry.boundsTree
      && !this.m_queued.has(geometry)
      && wantsBoundsTree(geometry);

    const { byIndex, overlapping } = analyzeIndexSharing(uses.keys());
    for (const geometry of uses.keys()) {
      if (!geometry.index && isCandidate(geometry)) this.queueJob([geometry], false);
    }
    for (const members of byIndex.values()) {
      const candidates = members.filter(isCandidate);
      if (candidates.length === 0) continue;
      const first = members[0]!;
      // One job for everything drawing from this index: it is sorted once, or
      // one triangle list serves them all. Only when EVERY user of the index
      // is in it — a user left out could not follow a sort.
      const oneJob = candidates.length === members.length
        && members.every((geometry) => !overlapping.has(geometry) && sameTriangleRanges(geometry, first));
      if (oneJob) {
        this.queueJob(members, members.every((geometry) => !uses.get(geometry)!.orderSensitive));
      } else {
        for (const geometry of candidates) this.queueJob([geometry], false);
      }
    }
    this.schedulePump();
  }

  /**
   * Drops everything not yet sent. Requests already in the worker still land
   * on their geometry if it is alive — harmless, and cheaper than cancelling.
   */
  clear(): void {
    this.m_queue = [];
    this.m_queueHead = 0;
    this.m_queued = new WeakSet<BufferGeometry>();
  }

  dispose(): void {
    this.clear();
    this.m_inFlight.clear();
    this.m_worker?.terminate();
    this.m_worker = null;
  }

  private queueJob(geometries: ReadonlyArray<BufferGeometry>, inPlace: boolean): void {
    for (const geometry of geometries) this.m_queued.add(geometry);
    this.m_queue.push({ geometries: geometries.map((geometry) => new WeakRef(geometry)), inPlace });
  }

  private schedulePump(): void {
    if (this.m_pumpScheduled || this.m_queueHead >= this.m_queue.length) return;
    this.m_pumpScheduled = true;
    // A task, not a microtask: the placement that queued this finishes and
    // draws before any copying starts.
    setTimeout(() => {
      this.m_pumpScheduled = false;
      this.pump();
    }, 0);
  }

  private pump(): void {
    while (this.m_inFlight.size < K_MAX_REQUESTS_IN_FLIGHT && this.m_queueHead < this.m_queue.length) {
      const worker = this.ensureWorker();
      if (!worker) return;
      const jobs: InFlightJob[] = [];
      const data: BoundsTreeJobData[] = [];
      const transfer: Transferable[] = [];
      let triangles = 0;
      while (
        this.m_queueHead < this.m_queue.length
        && jobs.length < K_MAX_GEOMETRIES_PER_REQUEST
        && triangles < K_MAX_TRIANGLES_PER_REQUEST
      ) {
        const queued = this.m_queue[this.m_queueHead++]!;
        const alive = queued.geometries
          .map((ref) => ref.deref())
          .filter((geometry): geometry is BufferGeometry =>
            geometry !== undefined && !geometry.boundsTree && wantsBoundsTree(geometry));
        if (alive.length === 0) continue;
        const owner = alive[0]!;
        const group = queued.geometries.length > 1;
        if (
          group
          && (alive.length !== queued.geometries.length
            || !owner.index
            || alive.some((geometry) => geometry.index !== owner.index))
        ) {
          // The group changed since it was queued; one build no longer fits
          // all of it. Build each with its own triangle list.
          for (const geometry of alive) this.m_queue.push({ geometries: [new WeakRef(geometry)], inPlace: false });
          continue;
        }
        const followers = alive.slice(1);
        const job = toJobData(owner, queued.inPlace && owner.index !== null, followers);
        jobs.push({
          members: [owner, ...followers].map((geometry) => {
            const position = geometry.attributes.position as BufferAttribute | InterleavedBufferAttribute;
            return { geometry: new WeakRef(geometry), position, positionVersion: attributeVersion(position) };
          }),
          index: owner.index,
          indexVersion: owner.index?.version ?? 0,
        });
        data.push(job);
        // Fresh slices (`toJobData`), so moving them empties nothing we keep.
        transfer.push(job.position.buffer as ArrayBuffer);
        for (const follower of job.followers) transfer.push(follower.position.buffer as ArrayBuffer);
        if (job.index) transfer.push(job.index.buffer as ArrayBuffer);
        triangles += triangleCount(owner) * (1 + followers.length);
      }
      this.compactQueue();
      if (jobs.length === 0) return;
      const request: BoundsTreeRequest = { id: this.m_nextRequestId++, jobs: data };
      this.m_inFlight.set(request.id, jobs);
      worker.postMessage(request, transfer);
    }
  }

  private compactQueue(): void {
    if (this.m_queueHead < 1024 || this.m_queueHead * 2 < this.m_queue.length) return;
    this.m_queue = this.m_queue.slice(this.m_queueHead);
    this.m_queueHead = 0;
  }

  private onResponse(response: BoundsTreeResponse): void {
    const jobs = this.m_inFlight.get(response.id);
    this.m_inFlight.delete(response.id);
    if (jobs) {
      response.results.forEach((result, i) => {
        const job = jobs[i];
        if (result && job) attach(job, result);
      });
    }
    // Straight from the message, not through a timer: a background tab clamps
    // timers to one a second, and the worker would idle between requests.
    this.pump();
  }

  private ensureWorker(): Worker | null {
    if (this.m_worker) return this.m_worker;
    if (this.m_unavailable) return null;
    try {
      const worker = new Worker(new URL("./boundsTree.worker.ts", import.meta.url), {
        type: "module",
        name: "campfire-bounds-tree",
      });
      worker.onmessage = (event: MessageEvent<BoundsTreeResponse>) => this.onResponse(event.data);
      worker.onerror = (event) => this.fail(event.message || event);
      worker.onmessageerror = (event) => this.fail(event);
      this.m_worker = worker;
      return worker;
    } catch (error) {
      this.fail(error);
      return null;
    }
  }

  private fail(reason: unknown): void {
    if (this.m_unavailable) return;
    console.warn(
      "[MeshBoundsTreeBuilder] spatial-index worker unavailable; picks keep three's own raycast",
      reason,
    );
    this.m_unavailable = true;
    this.dispose();
  }
}

/** All or nothing: a group's members draw from the one index this may sort. */
function attach(job: InFlightJob, result: BoundsTreeResultData): void {
  const geometries: BufferGeometry[] = [];
  for (const member of job.members) {
    const geometry = member.geometry.deref();
    if (!geometry || geometry.boundsTree) return;
    // A tree only fits the triangles it was built from.
    const position = geometry.attributes.position;
    if (position !== member.position || attributeVersion(position) !== member.positionVersion) return;
    if (geometry.index !== job.index || (geometry.index?.version ?? 0) !== job.indexVersion) return;
    geometries.push(geometry);
  }
  if (result.followerRoots.length !== geometries.length - 1) return;
  if (result.index) {
    // The tree's leaves are runs of the SORTED index. Write it over the
    // geometry's own array (the same memory, so nothing new stays allocated)
    // and have the renderer upload it whole: the same triangles, each with its
    // corners in the same order, only listed in a different sequence.
    const index = job.index;
    if (!index || index.array.length !== result.index.length) return;
    (index.array as IndexArray).set(result.index);
    index.clearUpdateRanges();
    index.needsUpdate = true;
  } else if (!result.indirectBuffer) {
    return;
  }
  geometries.forEach((geometry, i) => {
    const serialized = {
      version: 1,
      roots: i === 0 ? result.roots : result.followerRoots[i - 1]!,
      index: null,
      // One list for the whole group: every tree in it walks the same order.
      indirectBuffer: result.indirectBuffer,
    };
    geometry.boundsTree = MeshBVH.deserialize(serialized, geometry, { setIndex: false });
  });
}

/** Meshes whose class raycasts without `Mesh.raycast`, so a tree is never read. */
function hasOwnRaycast(mesh: Mesh): boolean {
  const flags = mesh as Mesh & { isSkinnedMesh?: boolean; isBatchedMesh?: boolean };
  return flags.isSkinnedMesh === true || flags.isBatchedMesh === true;
}

/**
 * Whether drawing with this material can show the ORDER of a mesh's triangles.
 * A see-through material composites overlapping triangles in the order they
 * are listed. A double-sided one draws back faces too, and where a back face
 * and a front face land at the same depth the LATER one wins the tie: sorting
 * Apollo 11 Hatch Door's indexes moved 14 of 6.2 M pixels (by up to 37 of 255)
 * where double-sided parts meet, and 0 once those kept their order
 * (2026-10-08). A single-sided, depth-tested, depth-writing material that does
 * not blend shows the nearest surface whatever the order.
 */
function isOrderSensitive(material: Material | Material[]): boolean {
  for (const each of Array.isArray(material) ? material : [material]) {
    const transmission = (each as Material & { transmission?: number }).transmission ?? 0;
    if (
      each.transparent
      || each.side === DoubleSide
      || !each.depthWrite
      || !each.depthTest
      || (each.blending !== NormalBlending && each.blending !== NoBlending)
      || transmission > 0
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Groups indexed geometries by the index attribute they use, and finds the
 * ones whose index memory overlaps a DIFFERENT attribute's (two accessors over
 * overlapping bytes of one buffer). Sorting one of those would reorder the
 * other's triangles with no tree of its own to match, so they keep a list.
 * Geometries that share one attribute outright are the in-place groups.
 */
function analyzeIndexSharing(geometries: Iterable<BufferGeometry>): {
  byIndex: Map<BufferAttribute, BufferGeometry[]>;
  overlapping: Set<BufferGeometry>;
} {
  const byIndex = new Map<BufferAttribute, BufferGeometry[]>();
  for (const geometry of geometries) {
    if (!geometry.index) continue;
    const members = byIndex.get(geometry.index);
    if (members) members.push(geometry);
    else byIndex.set(geometry.index, [geometry]);
  }
  const spansByBuffer = new Map<ArrayBufferLike, Array<{ start: number; end: number; index: BufferAttribute }>>();
  for (const index of byIndex.keys()) {
    const array = index.array;
    let spans = spansByBuffer.get(array.buffer);
    if (!spans) {
      spans = [];
      spansByBuffer.set(array.buffer, spans);
    }
    spans.push({ start: array.byteOffset, end: array.byteOffset + array.byteLength, index });
  }
  const overlapping = new Set<BufferGeometry>();
  const markAll = (index: BufferAttribute): void => {
    for (const geometry of byIndex.get(index)!) overlapping.add(geometry);
  };
  for (const spans of spansByBuffer.values()) {
    if (spans.length < 2) continue;
    spans.sort((a, b) => a.start - b.start);
    let furthest = spans[0]!;
    for (let i = 1; i < spans.length; i++) {
      const span = spans[i]!;
      if (span.start < furthest.end) {
        markAll(span.index);
        markAll(furthest.index);
      }
      if (span.end > furthest.end) furthest = span;
    }
  }
  return { byIndex, overlapping };
}

/** Same groups and draw range: the shared tree's roots are per group range. */
function sameTriangleRanges(a: BufferGeometry, b: BufferGeometry): boolean {
  if (a.drawRange.start !== b.drawRange.start || a.drawRange.count !== b.drawRange.count) return false;
  if (a.groups.length !== b.groups.length) return false;
  return a.groups.every((group, i) =>
    group.start === b.groups[i]!.start && group.count === b.groups[i]!.count);
}

/**
 * Whether a tree raycasts this geometry exactly as three's own loop does.
 * Three applies morph targets and the CURRENT draw range when it raycasts; a
 * tree holds the triangles of the moment it was built, and only the ones
 * inside the geometry's groups. So a morphing geometry, a partial draw range
 * and groups that leave gaps all keep three's own raycast.
 */
function wantsBoundsTree(geometry: BufferGeometry): boolean {
  const position = geometry.attributes.position;
  if (!position || geometry.morphAttributes.position !== undefined) return false;
  const vertexCount = geometry.index ? geometry.index.count : position.count;
  if (vertexCount / 3 < K_MIN_TRIANGLES) return false;
  if (geometry.drawRange.start !== 0 || geometry.drawRange.count < vertexCount) return false;
  return groupsCover(geometry.groups, vertexCount);
}

function groupsCover(groups: BufferGeometry["groups"], vertexCount: number): boolean {
  if (groups.length === 0) return true;
  const sorted = [...groups].sort((a, b) => a.start - b.start);
  let end = 0;
  for (const group of sorted) {
    if (group.start > end) return false;
    end = Math.max(end, group.start + group.count);
  }
  return end >= vertexCount;
}

function triangleCount(geometry: BufferGeometry): number {
  return (geometry.index ? geometry.index.count : geometry.attributes.position!.count) / 3;
}

function attributeVersion(attribute: BufferAttribute | InterleavedBufferAttribute): number {
  return (attribute as InterleavedBufferAttribute).isInterleavedBufferAttribute
    ? (attribute as InterleavedBufferAttribute).data.version
    : (attribute as BufferAttribute).version;
}

function toJobData(
  geometry: BufferGeometry,
  inPlace: boolean,
  followers: ReadonlyArray<BufferGeometry>,
): BoundsTreeJobData {
  const packed = packPositions(geometry.attributes.position as BufferAttribute | InterleavedBufferAttribute);
  return {
    position: packed.position,
    normalized: packed.normalized,
    index: geometry.index ? (geometry.index.array as IndexArray).slice() : null,
    groups: geometry.groups.map((group) => ({ start: group.start, count: group.count })),
    drawRange: { start: geometry.drawRange.start, count: geometry.drawRange.count },
    inPlace,
    followers: followers.map((follower) =>
      packPositions(follower.attributes.position as BufferAttribute | InterleavedBufferAttribute)),
  };
}

/**
 * A compact copy of the positions. The common case is a typed-array slice,
 * which copies exactly this geometry's range; an interleaved or non-xyz layout
 * is read component by component into floats instead.
 */
function packPositions(
  attribute: BufferAttribute | InterleavedBufferAttribute,
): { position: PositionArray; normalized: boolean } {
  const interleaved = (attribute as InterleavedBufferAttribute).isInterleavedBufferAttribute === true;
  if (!interleaved && attribute.itemSize === 3) {
    const position = (attribute.array as PositionArray).slice(0, attribute.count * 3) as PositionArray;
    return { position, normalized: attribute.normalized };
  }
  const position = new Float32Array(attribute.count * 3);
  for (let i = 0; i < attribute.count; i++) {
    position[i * 3] = attribute.getX(i);
    position[i * 3 + 1] = attribute.getY(i);
    position[i * 3 + 2] = attribute.getZ(i);
  }
  return { position, normalized: false };
}
